import type { TFunction } from 'i18next';

import type {
  ChannelType,
  CustomerBrief,
  CustomerDetails,
  Message,
  MessagePreview,
  SessionStatus,
} from '../types';

export const CHANNELS: ChannelType[] = ['widget', 'telegram-bot', 'viber-bot', 'telegram-user'];

/**
 * Why a message was not delivered, in the user's language: the server sends codes (`blocked`, `file-too-large:name`…)
 * for the reasons it knows; a channel's own description is shown as it is.
 */
export function deliveryErrorText(error: string | null | undefined, t: TFunction): string | null {
  if (!error) return null;
  const colon = error.indexOf(':');
  const code = colon > 0 ? error.slice(0, colon) : error;
  const detail = colon > 0 ? error.slice(colon + 1) : '';
  return t(`openlines:delivery.errors.${code}`, {
    detail,
    details: detail ? ` (${detail})` : '',
    defaultValue: error,
  });
}

/** Tailwind classes of a channel's badge (icon tile). */
export const CHANNEL_TONE: Record<ChannelType, string> = {
  widget: 'bg-brand-soft text-brand-text',
  'telegram-bot': 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'telegram-user': 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'viber-bot': 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
};

/** Line colors (Line.Colors on the server) → swatch classes. */
export const LINE_COLORS: Record<string, string> = {
  indigo: 'bg-indigo-500',
  blue: 'bg-blue-500',
  teal: 'bg-teal-500',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  orange: 'bg-orange-500',
  rose: 'bg-rose-500',
  violet: 'bg-violet-500',
  slate: 'bg-slate-500',
};

export const lineColorClass = (color: string) => LINE_COLORS[color] ?? 'bg-indigo-500';

export const isChannel = (value: string): value is ChannelType => (CHANNELS as string[]).includes(value);

/** The name of a client: the name they gave, their @username, else "Client #ab12". */
export function customerName(
  customer: Pick<CustomerBrief, 'id' | 'name' | 'username'>,
  t: TFunction,
): string {
  if (customer.name?.trim()) return customer.name.trim();
  if (customer.username?.trim()) return `@${customer.username.trim()}`;
  return t('openlines:customer.anonymous', { id: customer.id.slice(-4) });
}

const STATUSES: readonly SessionStatus[] = ['new', 'inWork', 'waiting', 'closed', 'spam'];

/** The dialog's state from a search document (`InWork` → `inWork`), or null for an unknown one. */
export function searchStatus(value: string | undefined): SessionStatus | null {
  if (!value) return null;
  const status = value.charAt(0).toLowerCase() + value.slice(1);
  return (STATUSES as readonly string[]).includes(status) ? (status as SessionStatus) : null;
}

export const isActiveStatus = (status: SessionStatus) =>
  status === 'new' || status === 'inWork' || status === 'waiting';

/** Badge variant of a dialog state. */
export function statusVariant(
  status: SessionStatus,
): 'info' | 'warning' | 'success' | 'secondary' | 'destructive' {
  switch (status) {
    case 'new':
      return 'warning';
    case 'inWork':
      return 'info';
    case 'waiting':
      return 'success';
    case 'spam':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/** The text of a system message in the reader's language. */
export function systemText(message: Pick<Message, 'systemType' | 'systemParams'>, t: TFunction): string {
  const p = message.systemParams ?? {};
  switch (message.systemType) {
    case 'started':
      return t('openlines:system.started', { channel: channelLabel(p.channel ?? '', t), line: p.line ?? '' });
    case 'assigned':
      return t('openlines:system.assigned', { name: p.operatorName ?? '' });
    case 'queued':
      return t('openlines:system.queued', { position: p.position ?? '' });
    case 'taken':
      return t('openlines:system.taken', { name: p.operatorName ?? '' });
    case 'transferred':
      return p.fromName
        ? t('openlines:system.transferred', { from: p.fromName, to: p.toName ?? '' })
        : t('openlines:system.transferredTo', { to: p.toName ?? '' });
    case 'moved-to-line':
      return t('openlines:system.movedToLine', { from: p.fromLine ?? '', to: p.toLine ?? '' });
    case 'timed-out':
      return t('openlines:system.timedOut', { name: p.operatorName ?? '' });
    case 'closed':
      return closedText(p, t);
    case 'reopened':
      return t('openlines:system.reopened', { name: p.operatorName ?? '' });
    case 'robot-reopened':
      return t('openlines:system.robotReopened');
    case 'robot-queued':
      return t('openlines:system.robotQueued');
    case 'rated':
      return p.comment
        ? t('openlines:system.ratedWithComment', { rating: p.rating ?? '', comment: p.comment })
        : t('openlines:system.rated', { rating: p.rating ?? '' });
    case 'crm-linked':
      // A request that became a deal (the CRM has no leads) says so; the older messages carry no record type and keep the lead wording.
      return p.outcome === 'contact'
        ? t('openlines:system.crmContact', { title: p.title ?? '' })
        : p.outcome === 'unconfirmed'
          ? t('openlines:system.crmUnconfirmed', { title: p.title ?? '' })
          : p.outcome === 'linked'
            ? t('openlines:system.crmLinked', {
                entity: t(`openlines:crm.types.${p.entityType ?? 'contact'}`, { defaultValue: p.entityType ?? '' }),
                title: p.title ?? '',
              })
            : p.outcome === 'attached'
              ? t(p.entityType === 'deal' ? 'openlines:system.crmAttachedDeal' : 'openlines:system.crmAttached')
              : t(p.entityType === 'deal' ? 'openlines:system.crmCreatedDeal' : 'openlines:system.crmCreated');
    case 'skipped':
      return t('openlines:system.skipped', { name: p.operatorName ?? '' });
    case 'customer-edited':
      return t('openlines:system.customerEdited', { name: p.operatorName ?? '' });
    default:
      return message.systemType ?? '';
  }
}

function closedText(p: Record<string, string>, t: TFunction): string {
  switch (p.reason) {
    case 'autoclose':
      return t('openlines:system.closedAuto');
    case 'customer':
      return t('openlines:system.closedByCustomer');
    case 'spam':
      return t('openlines:system.closedSpam', { name: p.byName ?? '' });
    case 'robot':
      return t('openlines:system.closedByRobot');
    default:
      return t('openlines:system.closed', { name: p.byName ?? '' });
  }
}

export function channelLabel(channel: string, t: TFunction): string {
  return isChannel(channel) ? t(`openlines:channels.${channel}`) : channel;
}

/** A one-line preview of the newest message of a dialog. */
export function previewText(preview: MessagePreview, t: TFunction): string {
  if (preview.kind === 'system')
    return systemText({ systemType: preview.systemType, systemParams: preview.systemParams ?? null }, t);
  const prefix =
    preview.kind === 'note'
      ? `${t('openlines:message.notePrefix')}: `
      : preview.author === 'operator'
        ? `${t('openlines:message.youPrefix')}: `
        : preview.author === 'bot'
          ? `${t(preview.systemType === 'robot' ? 'openlines:message.robotPrefix' : 'openlines:message.botPrefix')}: `
          : '';
  const text = preview.text || (preview.hasAttachments ? `📎 ${t('openlines:message.attachment')}` : '');
  return prefix + text;
}

/** "1 мин 05 с", "2 ч 10 мин", "45 с" from seconds. */
export function formatDuration(seconds: number | null | undefined, t: TFunction): string {
  if (seconds === null || seconds === undefined) return '—';
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return t('openlines:duration.seconds', { count: total });
  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const rest = total % 60;
    return rest
      ? t('openlines:duration.minutesSeconds', { minutes, seconds: rest })
      : t('openlines:duration.minutes', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes
    ? t('openlines:duration.hoursMinutes', { hours, minutes: restMinutes })
    : t('openlines:duration.hours', { count: hours });
}

/** The variables of automatic replies and quick answers (rendered by the server and the composer). */
export const TEMPLATE_VARIABLES = '{{client.name}}, {{operator.name}}, {{line.name}}';

/** Renders quick answer variables for the composer (the server renders automatic replies the same way). */
export function renderTemplate(
  template: string,
  values: { client?: string | null; operator?: string | null; line?: string | null },
): string {
  const map: Record<string, string | null | undefined> = {
    'client.name': values.client,
    'operator.name': values.operator,
    'line.name': values.line,
  };
  return template
    .replace(/\{\{\s*([a-z]+\.[a-z]+)\s*\}\}/gu, (match, name: string) =>
      name in map ? (map[name]?.trim() ?? '') : match,
    )
    .replace(/[ \t]{2,}/gu, ' ')
    .replace(/\s*,\s*([!.?])/gu, '$1')
    .trim();
}

export function customerContact(customer: CustomerDetails): string | null {
  return customer.phone ?? customer.email ?? (customer.username ? `@${customer.username}` : null);
}
